import {useEffect, useState} from "react";
import {distinctUntilChanged, map} from "rxjs";
import {OcrBatchIdResponse, OcrBatchResponse} from "../models/ocr.models";
import {useOcrBatch} from "../contexts/OcrBatchContext";
import {ocrService} from "../services/ocr.service";

export const useOcrBatchItem = (id: number) => {
    const { batches$, updateBatches, retryFileProcessing, exportFiles } = useOcrBatch();

    const [batch, setBatch] = useState<OcrBatchResponse | undefined>(
        () => batches$.getValue().find(b => b.id === id)
    );

    const [error, setError] = useState(false);

    // loading only when batch is not in cache
    const [loading, setLoading] = useState(
        () => !batches$.getValue().find(b => b.id === id)     );

    useEffect(() => {
        const sub = batches$.pipe(
            map(batches => batches.find(b => b.id === id)),
            distinctUntilChanged()
        ).subscribe(value => {
            if (value) {
                setBatch(value);
                setLoading(false);
            }
        });

        return () => sub.unsubscribe();
    }, [batches$, id]);

    useEffect(() => {
        // check out the cache
        if (batches$.getValue().find(b => b.id === id)) return;

        let cancelled = false;
        setLoading(true);
        setError(false);

        const fetchBatch = async () => {
            try {
                const response = await ocrService.getOcrBatch(id);
                const isStreamActive = batches$.getValue().some(b => b.id === id);

                const normalized = isStreamActive ? response : {
                    ...response,
                    messages: response.messages.map(msg => ({
                        ...msg,
                        files: msg.files.map(file =>
                            (file.status === 'PROCESSING' || file.status === 'PENDING')
                                ? { ...file, status: 'ERROR' as const }
                                : file
                        )
                    }))
                };

                if (!cancelled) {
                    updateBatches(prev => {
                        const exists = prev.some(b => b.id === id);
                        return exists ? prev : [...prev, normalized];
                    });
                }
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchBatch();

        return () => {
            cancelled = true;
        };
    }, [id]);

    return { batch, loading, error, exportFiles, retryFileProcessing };
};

export const useOcrHistory = () => {
    const { history$ } = useOcrBatch();
    const [history, setHistory] = useState<OcrBatchIdResponse[]>(history$.getValue());

    useEffect(() => {
        const sub = history$.subscribe(setHistory);
        return () => sub.unsubscribe();
    }, [history$]);

    return history;
};